import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { authFetch } from "@/lib/authFetch";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import {
  ShoppingBag, RefreshCw, Search, Package, CalendarDays,
  TrendingUp, Clock, Truck, MoreHorizontal, Eye, CheckCircle,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { CommandeFournisseur } from "@shared/schema";
import { useProductImageResolver } from "@/hooks/use-inventory";

const STATUTS: Record<string, { label: string; color: string }> = {
  "commandé":    { label: "Commandé",   color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" },
  "en_transit":  { label: "En transit", color: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300" },
  "en_douane":   { label: "En douane",  color: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300" },
  "reçu":        { label: "Reçu",       color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" },
  "annulé":      { label: "Annulé",     color: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400" },
};

function StatutBadge({ statut }: { statut: string | null }) {
  const s = statut || "commandé";
  const info = STATUTS[s] || { label: s, color: "bg-gray-100 text-gray-600" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${info.color}`}>
      {info.label}
    </span>
  );
}

function formatDate(d: string | null | undefined) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function formatMontant(m: string | number | null | undefined) {
  if (m === null || m === undefined) return "—";
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(m));
}

function LignesSummary({ lignes, resolveImage }: { lignes: any[]; resolveImage: (sku?: string | null, fallback?: string | null) => string | null }) {
  if (!lignes?.length) return <span className="text-muted-foreground text-xs">—</span>;
  const parts = lignes.slice(0, 4).map(l => `${l.quantite} ${l.nom?.split(" ")[0] || l.sku}`);
  const more = lignes.length > 4 ? ` +${lignes.length - 4}` : "";
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {lignes.slice(0, 3).map((l, i) => {
        const imgSrc = resolveImage(l.sku, l.imageUrl);
        return imgSrc ? (
          <img key={i} src={imgSrc} alt={l.nom} className="w-7 h-7 rounded object-cover border border-border" />
        ) : (
          <div key={i} className="w-7 h-7 rounded bg-muted flex items-center justify-center border border-border">
            <Package className="w-3.5 h-3.5 text-muted-foreground" />
          </div>
        );
      })}
      <span className="text-xs text-muted-foreground">{parts.join(" · ")}{more}</span>
    </div>
  );
}

function PaymentProgressBar({ echeances }: { echeances: any[] }) {
  if (!echeances?.length) return null;
  const total = echeances.reduce((s, e) => s + parseFloat(e.montant || "0"), 0);
  const paid = echeances.filter(e => e.statut === "payé").reduce((s, e) => s + parseFloat(e.montant || "0"), 0);
  const pct = total > 0 ? Math.round((paid / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2 min-w-[80px]">
      <Progress value={pct} className="h-1.5 flex-1" />
      <span className="text-xs text-muted-foreground whitespace-nowrap">{pct}%</span>
    </div>
  );
}

export default function AchatsList() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const resolveImage = useProductImageResolver();
  const [search, setSearch] = useState("");
  const [statut, setStatut] = useState("all");

  const { data: commandes, isLoading } = useQuery<CommandeFournisseur[]>({
    queryKey: ["/api/achats", statut, search],
    queryFn: () => {
      const params = new URLSearchParams();
      if (statut !== "all") params.set("statut", statut);
      if (search) params.set("search", search);
      return authFetch(`/api/achats?${params}`).then(r => r.json());
    },
  });

  const { data: stats } = useQuery({
    queryKey: ["/api/achats/stats"],
    queryFn: () => authFetch("/api/achats/stats").then(r => r.json()),
  });

  const syncMutation = useMutation({
    mutationFn: () => authFetch("/api/zoho/sync", { method: "POST" }).then(r => r.json()),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/achats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/achats/stats"] });
      toast({
        title: "Synchronisation terminée",
        description: `${data.totalPO || 0} commandes fournisseur synchronisées.`,
      });
    },
    onError: () => toast({ title: "Erreur de synchronisation", variant: "destructive" }),
  });

  const marquerRecuMutation = useMutation({
    mutationFn: (id: string) => authFetch(`/api/achats/${id}/marquer-recu`, { method: "POST" }).then(r => r.json()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/achats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/achats/stats"] });
      toast({ title: "Commande marquée comme reçue" });
    },
  });

  const kpiCards = [
    {
      label: "Commandes en cours",
      value: stats?.enCours ?? "—",
      icon: ShoppingBag,
      color: "text-blue-600",
      bg: "bg-blue-50 dark:bg-blue-950/20",
    },
    {
      label: "Montant en transit",
      value: stats ? formatMontant(stats.montantTransit) : "—",
      icon: Truck,
      color: "text-orange-600",
      bg: "bg-orange-50 dark:bg-orange-950/20",
    },
    {
      label: "Prochaine échéance",
      value: stats?.prochaineEcheance?.date
        ? `${formatDate(stats.prochaineEcheance.date)} · ${formatMontant(stats.prochaineEcheance.montant)}`
        : "Aucune",
      icon: Clock,
      color: "text-violet-600",
      bg: "bg-violet-50 dark:bg-violet-950/20",
    },
    {
      label: "Livraisons ce mois",
      value: stats?.livraisonsMonthCount ?? "—",
      icon: CalendarDays,
      color: "text-green-600",
      bg: "bg-green-50 dark:bg-green-950/20",
    },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShoppingBag className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Achats Pudu</h1>
            <p className="text-sm text-muted-foreground">
              Commandes fournisseur Pudu Robotics HK Limited
            </p>
          </div>
          {commandes && (
            <Badge variant="secondary" className="ml-2">
              {commandes.length}
            </Badge>
          )}
        </div>
        <Button
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
          data-testid="button-sync-achats"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${syncMutation.isPending ? "animate-spin" : ""}`} />
          {syncMutation.isPending ? "Synchronisation…" : "Synchroniser"}
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((kpi, i) => (
          <Card key={i} className="border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${kpi.bg}`}>
                  <kpi.icon className={`w-5 h-5 ${kpi.color}`} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{kpi.label}</p>
                  <p className="font-semibold text-sm leading-tight mt-0.5">{kpi.value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher PO, PI…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
            data-testid="input-search-achats"
          />
        </div>
        <Select value={statut} onValueChange={setStatut}>
          <SelectTrigger className="w-44" data-testid="select-statut-achats">
            <SelectValue placeholder="Tous statuts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            {Object.entries(STATUTS).map(([key, { label }]) => (
              <SelectItem key={key} value={key}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Liste */}
      <div className="border border-border rounded-xl overflow-hidden bg-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">N° PO / PI</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Date commande</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Livraison prévue</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Produits</th>
                <th className="px-4 py-3 text-right font-medium text-muted-foreground">Montant HT</th>
                <th className="px-4 py-3 text-center font-medium text-muted-foreground">Statut</th>
                <th className="px-4 py-3 text-center font-medium text-muted-foreground">Paiement</th>
                <th className="px-4 py-3 text-center font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 8 }).map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <Skeleton className="h-4 w-full" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : !commandes?.length ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-muted-foreground">
                    <ShoppingBag className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p>Aucune commande fournisseur</p>
                    <p className="text-xs mt-1">Synchronisez depuis Zoho Inventory pour importer les PO</p>
                  </td>
                </tr>
              ) : (
                commandes.map(c => {
                  const echeances = (c.echeancesPaiement as any[]) || [];
                  return (
                    <tr
                      key={c.id}
                      className="hover:bg-muted/30 cursor-pointer transition-colors"
                      onClick={() => navigate(`/achats/${c.id}`)}
                      data-testid={`row-achat-${c.id}`}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium">{c.numeroPo || "—"}</div>
                        {c.numeroPi && <div className="text-xs text-muted-foreground">{c.numeroPi}</div>}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{formatDate(c.dateCommande)}</td>
                      <td className="px-4 py-3 text-muted-foreground">{formatDate(c.dateLivraisonPrevue)}</td>
                      <td className="px-4 py-3">
                        <LignesSummary lignes={(c.lignes as any[]) || []} resolveImage={resolveImage} />
                      </td>
                      <td className="px-4 py-3 text-right font-medium">{formatMontant(c.montantTotal)}</td>
                      <td className="px-4 py-3 text-center">
                        <StatutBadge statut={c.statut} />
                      </td>
                      <td className="px-4 py-3">
                        <PaymentProgressBar echeances={echeances} />
                      </td>
                      <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/achats/${c.id}`)}>
                              <Eye className="w-4 h-4 mr-2" /> Voir le détail
                            </DropdownMenuItem>
                            {c.statut !== "reçu" && c.statut !== "annulé" && (
                              <DropdownMenuItem onClick={() => marquerRecuMutation.mutate(c.id)}>
                                <CheckCircle className="w-4 h-4 mr-2" /> Marquer comme reçu
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
